import { useMutation } from '@apollo/client';
import {
  Alert,
  Box,
  Button,
  Card,
  CheckboxStandalone,
  Column,
  Columns,
  Heading,
  Inline,
  List,
  Stack,
  Text,
  TextLink,
} from 'braid-design-system';
import React, { useState } from 'react';

import CustomLoader from 'src/components/CustomLoader/CustomLoader';
import { Error as GenericError } from 'src/components/Error/Error';
import { getConfigValues } from 'src/config';
import Routes from 'src/config/routes';
import {
  ListingProductCode,
  PayPerEnquiryRegistrationDocument,
} from 'src/generated/graphql';
import { fireListingCoreProductPurchasesToHeap } from 'src/utils/analytics';

import { PaymentContainer } from '../PaymentContainer';

export const PayPerEnquiryContract = () => {
  const [checked, setChecked] = useState<boolean>(false);
  const [displayErrorMessage, setDisplayErrorMessage] =
    useState<boolean>(false);

  const [payPerEnquiryRegistration, { loading, error }] = useMutation(
    PayPerEnquiryRegistrationDocument,
  );

  const onSubmit = async () => {
    if (!checked) {
      setDisplayErrorMessage(true);
      return;
    }

    try {
      await payPerEnquiryRegistration();
    } catch {
      // Do nothing, we'll handle via the mutation's error
      return;
    }

    // This is so that we can track conversions of PPE clients the same way as others
    fireListingCoreProductPurchasesToHeap(ListingProductCode.PayPerEnquiry);

    const casbaUrl =
      getConfigValues().ADVERTISER_CENTRE_URL + Routes.CREATE_AD_URL;
    window.location.assign(casbaUrl);
  };

  if (loading) {
    return (
      <PaymentContainer>
        <Box data={{ testid: 'ppe-payment-success-loader' }}>
          <CustomLoader />
        </Box>
      </PaymentContainer>
    );
  }

  if (error) {
    return (
      <PaymentContainer>
        <GenericError />
      </PaymentContainer>
    );
  }

  return (
    <PaymentContainer>
      <Card>
        <Stack space="gutter">
          <Box paddingBottom="medium">
            <Heading level="2" weight="weak">
              Pay Per Enquiry terms
            </Heading>
          </Box>
          <Heading level="4" weight="weak">
            Maximum charge and monthly invoice details
          </Heading>

          <List>
            <Text>
              “Pay Per Enquiry” means you will not be charged an upfront fee for
              listing an advertisement on the SEEK Business website. Once the
              advertisement is created and posted online, you will be invoiced
              on a monthly basis for any Eligible Enquiries received during the
              preceding month, at a cost of $80 (+GST) per Eligible Enquiry.
            </Text>
            <Text>
              The maximum you will be charged over the lifetime of your
              advertisement is $960 (+GST), being the cost of 12 Eligible
              Enquiries. Once you have paid for 12 Eligible Enquiries, you will
              not be invoiced for any further Eligible Enquiries you receive.
            </Text>
          </List>

          <Alert tone="info">
            <Stack space="gutter">
              <Text>
                You will only be charged for Eligible Enquiries. An “Eligible
                Enquiry” means an enquiry which:
              </Text>
              <List space="medium">
                <Text>includes a valid phone number or email address;</Text>
                <Text>
                  is not generated by a bot or classified as spam; and
                </Text>
                <Text>
                  is not a repetition of any previously received enquiry.
                </Text>
              </List>
            </Stack>
          </Alert>

          <List>
            <Text>
              An enquiry is considered an “Eligible Enquiry” regardless of the
              outcome between the buyer and seller.
            </Text>
            <Text>
              For any further questions, please email us at{' '}
              <TextLink
                weight="weak"
                href="mailto:enquiries@seekbusiness.com.au"
              >
                enquiries@seekbusiness.com.au
              </TextLink>
              .
            </Text>
          </List>

          <Columns space="gutter">
            <Column width="content">
              <CheckboxStandalone
                id="contractCheckbox"
                aria-label="contractCheckbox"
                data={{ testid: 'contract-checkbox' }}
                checked={checked}
                onChange={() => {
                  setChecked(!checked);
                  setDisplayErrorMessage(false);
                }}
                tone={displayErrorMessage ? 'critical' : 'neutral'}
              />
            </Column>
            <Column>
              <Stack space="small">
                <Stack space="medium">
                  <Text>
                    You warrant that you have the legal authority to enter into
                    this agreement on behalf of your organisation and
                    acknowledge and agree that you have read and understood all
                    the{' '}
                    <TextLink
                      href="https://www.seekbusiness.com.au/terms-and-conditions"
                      target="_blank"
                    >
                      Terms and Conditions
                    </TextLink>{' '}
                    of this agreement (including the{' '}
                    <TextLink
                      href="https://www.seekbusiness.com.au/contract-terms"
                      target="_blank"
                    >
                      Advertising Terms
                    </TextLink>
                    ) and are aware that the terms contain limitations on SEEK’s
                    liability.
                  </Text>
                  <Text>
                    By continuing, you are entering into an agreement with SEEK
                    and will be bound by the terms and conditions contained
                    herein.
                  </Text>
                </Stack>
                {displayErrorMessage && (
                  <Text tone="critical">
                    <i>
                      You must tick the checkbox in order to accept these terms.
                    </i>
                  </Text>
                )}
              </Stack>
            </Column>
          </Columns>

          <Inline space="small">
            <Button
              // Realistically we shouldn't see this button when loading due to
              // the above `if (loading)` check but I'm putting this here just in case
              loading={loading}
              type="submit"
              variant={checked ? 'solid' : 'ghost'}
              onClick={(e) => {
                e.preventDefault();
                onSubmit();
              }}
              data={{
                htag: 'ppe-accept-cta',
                analytics: 'ppe-accept-cta',
              }}
            >
              Accept terms and continue
            </Button>
          </Inline>
        </Stack>
      </Card>
    </PaymentContainer>
  );
};
